import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Send } from "lucide-react";

import { issueCommand, listCommands } from "@/api/fleet";
import { useToast } from "@/contexts/ToastContext";
import { formatDate } from "@/lib/utils";

const COMMAND_TYPES = [
  { id: "pausar_lectura", label: "Pausar lectura", needsPayload: false },
  { id: "reanudar_lectura", label: "Reanudar lectura", needsPayload: false },
  { id: "asignar_operativo", label: "Asignar operativo", needsPayload: true },
  { id: "cerrar_operativo", label: "Cerrar operativo", needsPayload: false },
  { id: "actualizar_config", label: "Actualizar config", needsPayload: true },
  { id: "rotar_token", label: "Rotar token", needsPayload: false },
  { id: "reiniciar_servicios", label: "Reiniciar servicios", needsPayload: false },
] as const;

interface Props {
  unitId: number;
}

export function CommandsTab({ unitId }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [tipo, setTipo] = useState<string>(COMMAND_TYPES[0].id);
  const [payload, setPayload] = useState<string>("");

  const cmds = useQuery({
    queryKey: ["unit-commands", unitId],
    queryFn: () => listCommands(unitId),
    refetchInterval: 5_000,
  });

  const issueM = useMutation({
    mutationFn: (body: { tipo: string; payload?: Record<string, unknown> }) =>
      issueCommand(unitId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["unit-commands", unitId] });
      toast?.push("Comando enviado.", "info");
    },
    onError: (err) => toast?.push(`Error: ${String(err)}`, "error"),
  });

  const cur = COMMAND_TYPES.find((c) => c.id === tipo);

  return (
    <section className="space-y-3" data-testid="unit-commands">
      <h2 className="text-sm font-semibold">Comandos</h2>

      <div className="card space-y-2">
        <label className="text-xs flex flex-col">
          <span className="text-muted-200 mb-1">Tipo de comando</span>
          <select
            className="select"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            {COMMAND_TYPES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        {cur?.needsPayload && (
          <label className="text-xs flex flex-col">
            <span className="text-muted-200 mb-1">Payload JSON</span>
            <textarea
              className="input"
              rows={3}
              value={payload}
              onChange={(e) => setPayload(e.target.value)}
              placeholder='{"operativo_id": 42}'
            />
          </label>
        )}
        <button
          className="btn-primary text-xs"
          onClick={() => {
            let parsed: Record<string, unknown> | undefined;
            if (cur?.needsPayload && payload.trim()) {
              try {
                parsed = JSON.parse(payload);
              } catch {
                toast?.push("Payload JSON inválido", "error");
                return;
              }
            }
            issueM.mutate({ tipo, payload: parsed });
          }}
          disabled={issueM.isPending}
        >
          <Send size={14} /> Enviar comando
        </button>
      </div>

      <div className="card">
        <h3 className="text-sm font-medium mb-2">Recientes</h3>
        <ul className="space-y-1 text-sm">
          {(cmds.data ?? []).slice(0, 10).map((c) => (
            <li
              key={c.id}
              className="flex items-baseline justify-between gap-2 text-xs"
            >
              <span>{c.tipo}</span>
              <span className="text-muted-200">{formatDate(c.creado_at)}</span>
            </li>
          ))}
          {(cmds.data ?? []).length === 0 && (
            <li className="text-xs text-muted-200">Sin comandos.</li>
          )}
        </ul>
      </div>
    </section>
  );
}
